import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import { Add, Close, DeleteOutline, FilterList } from '@mui/icons-material';
import { FieldFilters } from './useFieldFilters';
import { Filter } from './typings';
import { FormControl as FormControlOur } from '../FormControl';
import { EmptyState } from '../../primitives/EmptyState';
import { Tooltip } from '../../primitives/Tooltip';
import { ExtraProps } from '../FormControlProps';
import { EditSavedFiltersDialog, FiltersToSaveDefaults, SavedFiltersSelection, SaveFiltersDialog, useSavedFilters } from './useSavedFilters';
import isHotkey from 'is-hotkey';
import { Buttons } from '../../primitives';
import { StrippedIconButton } from '../../primitives/StrippedButtons';
import { AutoSuggestMultiple } from '../../primitives/AutoSuggests';
import { FieldType } from '../../../hooks/useSchema';

interface Props {
    fieldFilters: FieldFilters;
}

interface DialogProps extends Props {
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
}

interface FilterProps {
    filter: Filter;
    fieldFilters: FieldFilters;
}

const FilterRow = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 3fr 200px 6fr max-content;
    gap: 12px;
    align-items: flex-end;

    @media(max-width: 776px) {
        grid-template-columns: 1fr;
    }

    & > * {
        overflow: hidden;
    }

    & + & {
      .MuiFormLabel-root {
        display: none;
      }
    }
`;

const FieldControlExtraProps: ExtraProps = { autoComplete: true, controlProps: { autoFocus: true } };
const WithAutoComplete: ExtraProps = { autoComplete: true };
const WithAutoCompleteAutosuggestMultiple: ExtraProps = { autoComplete: true, control: AutoSuggestMultiple, suggestionsKey: "x" };

const FilterEditor = ({ filter, fieldFilters}: FilterProps) => {
    const filterSchemas = fieldFilters.filterSchemas(filter);

    return (
        <FilterRow>
            <FormControlOur
                row={filter}
                field="field"
                schema={filterSchemas.field}
                onChange={(old, changes) => fieldFilters.update(old, changes)}
                extraProps={FieldControlExtraProps}
                />
            <FormControlOur
                row={filter}
                field="op"
                schema={filterSchemas.op}
                onChange={(old, changes) => fieldFilters.update(old, changes)}
                extraProps={WithAutoComplete}
            />
            {filterSchemas.needsValue ?
                <FormControlOur
                    row={filter}
                    field="value"
                    schema={filterSchemas.value}
                    extraProps={filterSchemas.value?.type === FieldType.multiselect && !filterSchemas.value?.values?.length ? WithAutoCompleteAutosuggestMultiple : WithAutoComplete}
                    onChange={(old, changes) => fieldFilters.update(old, changes)}
                />
                : <div />}
            <div>
                <StrippedIconButton onClick={() => fieldFilters.remove(filter)}>
                    <DeleteOutline />
                </StrippedIconButton>
            </div>
        </FilterRow>
    );
}

export const FieldFiltersPopup = ({ isOpen, setIsOpen, fieldFilters }: DialogProps) => {
    const { filters } = fieldFilters;
    const savedFilters = useSavedFilters(fieldFilters.userSettingsKey || "");

    useEffect(() => {
      if(isOpen) {
        savedFilters.ensureLoaded();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
      if(isOpen) {
        
        const realHandler = (e: KeyboardEvent) => {
          if(isHotkey("mod+Enter", e)) {
            fieldFilters.add();
          }
        }
        window.addEventListener("keydown", realHandler);
        return () => window.removeEventListener("keydown", realHandler);
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

    return (<>
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth={"md"} fullWidth >
            <DialogTitle>
              <Buttons>
                <Typography variant="h6">
                  <FormattedMessage id="fieldsfilters.title" />
                </Typography>

                <Tooltip text="Ctrl + Enter">
                  <IconButton onClick={fieldFilters.add}>
                    <Add />
                  </IconButton>
                </Tooltip>
              </Buttons>
                
            </DialogTitle>
            <DialogContent>
                {!filters.length && <EmptyState text={<FormattedMessage id="fieldsfilters.emptyState" />} onClick={fieldFilters.add} />}
                {filters.map(f => <FilterEditor key={f.id} filter={f} fieldFilters={fieldFilters} />)}

                <SavedFiltersSelection
                  data={savedFilters}
                  selectFilter={f => fieldFilters.set(f.filters, true)}
                  appendFilter={f => fieldFilters.set([ ...fieldFilters.filters, ...f.filters ], true)}
                  />
            </DialogContent>
            <DialogActions>
                {!!filters.length && savedFilters.isAvailable &&
                  <Button onClick={() => savedFilters.saveFilters.startEditing({ ...FiltersToSaveDefaults, filters })}><FormattedMessage id="common.save" /></Button>}
                <Button onClick={() => fieldFilters.clear()}><FormattedMessage id="fieldssettings.reset" /></Button>
                <Button color="primary" onClick={() => setIsOpen(false)}><FormattedMessage id="common.close" /></Button>
            </DialogActions>
        </Dialog>

      {savedFilters.isAvailable && <SaveFiltersDialog data={savedFilters.saveFilters} isGlobalAvailable={savedFilters.canUpdateGlobal} />}
      {savedFilters.isAvailable && <EditSavedFiltersDialog data={savedFilters.editFilters} remove={savedFilters.removeFilters.run} />}
    </>);
}


const FilterButtonWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const TinyResetBadge = styled(IconButton)`
    position: absolute;
    top: -2px;
    right: -2px;
    width: 14px;
    height: 14px;
    padding: 0;
    background-color: #d32f2f;
    color: #ffffff;
    box-shadow: 0 0 0 1.5px #ffffff;
    z-index: 1;

    &:hover {
        background-color: #9a0007;
    }

    & svg {
        font-size: 10px;
    }
`;

interface ButtonProps extends Props {
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
}

export const FiltersButton = ({ fieldFilters, isOpen, setIsOpen }: ButtonProps) => {
    return (
      <FilterButtonWrapper>
        <Tooltip text_id="fieldsfilters.title">
          <IconButton
            size="small"
            onClick={() => setIsOpen(!isOpen)}
            color={fieldFilters.isActive ? 'primary' : 'default'}>
            <FilterList />
          </IconButton>
        </Tooltip>

        {fieldFilters.isActive && (
          <Tooltip text_id="fieldssettings.reset">
            <TinyResetBadge
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                fieldFilters.clear();
              }}>
              <Close />
            </TinyResetBadge>
          </Tooltip>
        )}
      </FilterButtonWrapper>
      );
}

export const FieldFiltersPopupButton = (props: Props) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return <>
        <FiltersButton {...props} isOpen={isOpen} setIsOpen={setIsOpen} />
        <FieldFiltersPopup isOpen={isOpen} setIsOpen={setIsOpen} {...props} />
    </>;
}

const FilterPanelWrapper = styled.div`
    display: flex;
    flex-flow: column;

    & > div:nth-child(1) {
        display: flex;
        flex-flow: row wrap;
    }

    & > div:nth-child(2) {
        display: flex;
        flex-flow: row;
        justify-content: flex-end;
    }
`;

interface PanelProps extends Props {
    hideButtons?: boolean;
    noSavedFilters?: boolean;
}

export const FieldFiltersPanel = ({ fieldFilters, noSavedFilters, hideButtons }: PanelProps) => {
    const savedFilters = useSavedFilters(fieldFilters.userSettingsKey || "");

    useEffect(() => {
      if(!noSavedFilters) {
        savedFilters.ensureLoaded();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noSavedFilters]);

    return <FilterPanelWrapper>
        <div>
            {fieldFilters.filters.map(f => <FilterEditor key={f.id} filter={f} fieldFilters={fieldFilters} />)}
            {!noSavedFilters &&
              <SavedFiltersSelection
                data={savedFilters}
                selectFilter={f => fieldFilters.set(f.filters)}
                appendFilter={f => fieldFilters.set([ ...fieldFilters.filters, ...f.filters ])}
                />}
            {savedFilters.isAvailable && <SaveFiltersDialog data={savedFilters.saveFilters} isGlobalAvailable={savedFilters.canUpdateGlobal} />}
            {savedFilters.isAvailable && <EditSavedFiltersDialog data={savedFilters.editFilters} remove={savedFilters.removeFilters.run} />}
        </div>
        <div>
            {!hideButtons && <>
                {!!fieldFilters.filters.length && savedFilters.isAvailable &&
                    <Button onClick={() => savedFilters.saveFilters.startEditing({ ...FiltersToSaveDefaults, filters: fieldFilters.filters })}><FormattedMessage id="common.save" /></Button>}
                <Button onClick={() => fieldFilters.clear()}><FormattedMessage id="fieldssettings.reset" /></Button>
                <Button color="primary" onClick={fieldFilters.add}><FormattedMessage id="fieldsfilters.add" /></Button>
            </>}
        </div>
    </FilterPanelWrapper>;
}
